/** Persisted Markdown preview appearance + parsing (see Settings → Markdown preview style…). */

export type MarkdownLineBreakMode = "commonmark" | "gfm";

export type MarkdownFontScaleId = "compact" | "normal" | "comfortable";

export type MarkdownContentWidthId = "full" | "readable" | "article";

export type MarkdownProseFontId = "sans" | "serif";

/** Relative scale for h1–h6 (em, relative to preview body font size). */
export type MarkdownHeadingScalePreset = "compact" | "balanced" | "editorial" | "prominent";

/** Heading typeface: follow body (sans/serif) or override. */
export type MarkdownHeadingFontRole = "inherit" | "sans" | "serif";

export type MarkdownCodeFontPreset = "menlo" | "fira" | "jetbrains" | "source";

export type MarkdownCodeBlockTone = "muted" | "plain" | "high-contrast";

export type MarkdownCodeSizePreset = "small" | "medium" | "large";

export type MarkdownBlockquoteTone = "subtle" | "normal" | "strong";

export type MarkdownLinkTone = "blue" | "accent" | "neutral-underline";

export type MarkdownTableStyle = "bordered" | "minimal" | "striped";

export type MarkdownTableDensity = "compact" | "normal" | "comfortable";

export type MarkdownParagraphSpacing = "compact" | "normal" | "relaxed";

export type MarkdownListDensity = "compact" | "normal" | "relaxed";

export type MarkdownPreviewStyle = {
  lineBreaks: MarkdownLineBreakMode;
  fontScale: MarkdownFontScaleId;
  contentWidth: MarkdownContentWidthId;
  proseFont: MarkdownProseFontId;
  headingScale: MarkdownHeadingScalePreset;
  headingFont: MarkdownHeadingFontRole;
  codeFont: MarkdownCodeFontPreset;
  codeBlockTone: MarkdownCodeBlockTone;
  codeSize: MarkdownCodeSizePreset;
  blockquoteTone: MarkdownBlockquoteTone;
  linkTone: MarkdownLinkTone;
  tableStyle: MarkdownTableStyle;
  tableDensity: MarkdownTableDensity;
  paragraphSpacing: MarkdownParagraphSpacing;
  listDensity: MarkdownListDensity;
};

export const defaultMarkdownPreviewStyle: MarkdownPreviewStyle = {
  lineBreaks: "commonmark",
  fontScale: "normal",
  contentWidth: "full",
  proseFont: "sans",
  headingScale: "balanced",
  headingFont: "inherit",
  codeFont: "menlo",
  codeBlockTone: "muted",
  codeSize: "medium",
  blockquoteTone: "normal",
  linkTone: "blue",
  tableStyle: "bordered",
  tableDensity: "normal",
  paragraphSpacing: "normal",
  listDensity: "normal",
};

function isHeadingScale(v: unknown): v is MarkdownHeadingScalePreset {
  return v === "compact" || v === "balanced" || v === "editorial" || v === "prominent";
}

function isHeadingFont(v: unknown): v is MarkdownHeadingFontRole {
  return v === "inherit" || v === "sans" || v === "serif";
}

function isCodeFont(v: unknown): v is MarkdownCodeFontPreset {
  return v === "menlo" || v === "fira" || v === "jetbrains" || v === "source";
}

function isCodeBlockTone(v: unknown): v is MarkdownCodeBlockTone {
  return v === "muted" || v === "plain" || v === "high-contrast";
}

function isCodeSize(v: unknown): v is MarkdownCodeSizePreset {
  return v === "small" || v === "medium" || v === "large";
}

function isBlockquoteTone(v: unknown): v is MarkdownBlockquoteTone {
  return v === "subtle" || v === "normal" || v === "strong";
}

function isLinkTone(v: unknown): v is MarkdownLinkTone {
  return v === "blue" || v === "accent" || v === "neutral-underline";
}

function isTableStyle(v: unknown): v is MarkdownTableStyle {
  return v === "bordered" || v === "minimal" || v === "striped";
}

function isTableDensity(v: unknown): v is MarkdownTableDensity {
  return v === "compact" || v === "normal" || v === "comfortable";
}

function isParagraphSpacing(v: unknown): v is MarkdownParagraphSpacing {
  return v === "compact" || v === "normal" || v === "relaxed";
}

function isListDensity(v: unknown): v is MarkdownListDensity {
  return v === "compact" || v === "normal" || v === "relaxed";
}

export function mergeMarkdownPreviewStyle(raw: unknown): MarkdownPreviewStyle {
  if (!raw || typeof raw !== "object") {
    return { ...defaultMarkdownPreviewStyle };
  }
  const o = raw as Record<string, unknown>;
  const base = { ...defaultMarkdownPreviewStyle };
  return {
    lineBreaks: o.lineBreaks === "gfm" ? "gfm" : "commonmark",
    fontScale:
      o.fontScale === "compact" || o.fontScale === "comfortable" ? o.fontScale : "normal",
    contentWidth:
      o.contentWidth === "readable" || o.contentWidth === "article" ? o.contentWidth : "full",
    proseFont: o.proseFont === "serif" ? "serif" : "sans",
    headingScale: isHeadingScale(o.headingScale) ? o.headingScale : base.headingScale,
    headingFont: isHeadingFont(o.headingFont) ? o.headingFont : base.headingFont,
    codeFont: isCodeFont(o.codeFont) ? o.codeFont : base.codeFont,
    codeBlockTone: isCodeBlockTone(o.codeBlockTone) ? o.codeBlockTone : base.codeBlockTone,
    codeSize: isCodeSize(o.codeSize) ? o.codeSize : base.codeSize,
    blockquoteTone: isBlockquoteTone(o.blockquoteTone) ? o.blockquoteTone : base.blockquoteTone,
    linkTone: isLinkTone(o.linkTone) ? o.linkTone : base.linkTone,
    tableStyle: isTableStyle(o.tableStyle) ? o.tableStyle : base.tableStyle,
    tableDensity: isTableDensity(o.tableDensity) ? o.tableDensity : base.tableDensity,
    paragraphSpacing: isParagraphSpacing(o.paragraphSpacing) ? o.paragraphSpacing : base.paragraphSpacing,
    listDensity: isListDensity(o.listDensity) ? o.listDensity : base.listDensity,
  };
}
